import { create } from 'zustand';
import type { ToastMessage, ToastType } from '../types';

interface ToastStoreState {
  toasts: ToastMessage[];
}

interface ToastStoreActions {
  addToast: (toast: Omit<ToastMessage, 'id'>) => string;
  removeToast: (id: string) => void;
  toast: {
    success: (title: string, description?: string) => string;
    error: (title: string, description?: string) => string;
    warning: (title: string, description?: string) => string;
    info: (title: string, description?: string) => string;
  };
}

type ToastStore = ToastStoreState & ToastStoreActions;

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 5000,
  error: 0, // Persist until dismissed
  warning: 7000,
  info: 5000,
};

export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = crypto.randomUUID();
    const duration = toast.duration ?? DEFAULT_DURATIONS[toast.type];

    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));

    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  toast: {
    success: (title, description) =>
      get().addToast({ type: 'success', title, description }),
    error: (title, description) =>
      get().addToast({ type: 'error', title, description }),
    warning: (title, description) =>
      get().addToast({ type: 'warning', title, description }),
    info: (title, description) =>
      get().addToast({ type: 'info', title, description }),
  },
}));

// Convenience function for components
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().toast.success(title, description),
  error: (title: string, description?: string) =>
    useToastStore.getState().toast.error(title, description),
  warning: (title: string, description?: string) =>
    useToastStore.getState().toast.warning(title, description),
  info: (title: string, description?: string) =>
    useToastStore.getState().toast.info(title, description),
};
